import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AGENT } from "../data/site";
import { scrollToId } from "./RouteScroll";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "About", id: "about" },
  { label: "Guides", id: "guides" },
  { label: "FAQ", id: "faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (loc.pathname !== "/") {
      setActive("");
      return;
    }
    const ids = [...LINKS.map((l) => l.id), "contact"];
    const onScroll = () => {
      const line = window.innerHeight * 0.35;
      let current = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loc.pathname]);

  const go = (id) => {
    setOpen(false);
    if (loc.pathname !== "/") {
      nav(`/#${id}`);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#F9F6F0]/80 backdrop-blur-xl border-b border-[#D8D0C5]/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
          <span className="font-display text-2xl tracking-tight text-[#1A1714]">{AGENT.logoTop}</span>
          <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[#C85A40] font-bold whitespace-nowrap" data-testid="nav-logo-sub">{AGENT.logoBottom}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              data-active={active === l.id}
              onClick={() => go(l.id)}
              className={`text-sm font-medium transition-colors hover:text-[#C85A40] ${
                active === l.id ? "text-[#C85A40]" : "text-[#4A453F]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center rounded-full bg-[#C85A40] px-5 py-2.5 text-sm font-semibold text-[#F9F6F0] hover:bg-[#A64730] active:scale-95 transition-[background-color,transform] duration-200"
          >
            Get a free quote
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-[#1A1714]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#F9F6F0] border-b border-[#D8D0C5] px-5 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="text-left text-lg font-display text-[#1A1714]">
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            className="mt-2 rounded-full bg-[#C85A40] px-5 py-3 text-sm font-semibold text-[#F9F6F0]"
          >
            Get a free quote
          </button>
        </div>
      )}
    </header>
  );
}
